import type { ModulationPlan } from '@tunasong/music-lib'
import { chordToEvents } from './chord-to-events.js'
import type { Stab } from './stab-scheduler.js'

/**
 * Modulation Lab audition primitive. Pure function — emits a chronologically
 * ordered list of `Stab`s for the 3-phase modulation contract (source vamp →
 * modulation phase → destination vamp). Parallel to `section-sequencer.ts` —
 * factored separately so the Modulation Lab's phase typing stays explicit.
 *
 * Per-phase timing model (v1 locks 4+4+4 measures at 100 BPM):
 *  - Each phase is 4 measures × 2400ms/measure = 9600ms.
 *  - 250ms inter-phase gap (50ms pre + 200ms post on the boundary —
 *    same model as `section-sequencer.ts`).
 *  - Total duration: 3 × 9600 + 2 × 250 = 29900ms.
 *
 * Special case for parallel-mode technique: the modulation-phase chords play
 * with the 3rd in the top voice — implemented by octave=5 override for those
 * stabs (Bowie §8.4 voicing requirement). Source and destination vamps stay at
 * the default octave 4.
 *
 * Spec: docs/superpowers/specs/2026-05-26-modulation-lab-v1-design.md §4
 */

const QUARTERS_PER_MEASURE = 4
const PHASE_MEASURES = 4
const PRE_ROLL_MS = 50
const POST_ROLL_MS = 200
const SECTION_GAP_MS = PRE_ROLL_MS + POST_ROLL_MS

const CHORD_DURATION = '2n' as const
const PARALLEL_VOICE_OCTAVE = 5
const DEFAULT_OCTAVE = 4

export interface ModulationSequencerInput {
  plan: ModulationPlan
  /** BPM — v1 locks this at 100. */
  bpm: number
}

export interface ModulationPhaseBoundaries {
  sourceStartMs: number
  modulationStartMs: number
  destinationStartMs: number
  endMs: number
}

export interface ModulationStabsResult {
  stabs: Stab[]
  phaseBoundaries: ModulationPhaseBoundaries
}

/** Pure stab emitter. Deterministic given `plan` + `bpm`. */
export const buildModulationStabs = (input: ModulationSequencerInput): ModulationStabsResult => {
  const { plan, bpm } = input
  const quarterMs = 60000 / bpm
  const measureMs = QUARTERS_PER_MEASURE * quarterMs
  const phaseMs = PHASE_MEASURES * measureMs

  const stabs: Stab[] = []

  const emitSection = (
    chords: ModulationPlan['sourceProgression'],
    startMs: number,
    octave: number
  ): void => {
    const chordSlotMs = phaseMs / chords.length
    for (let i = 0; i < chords.length; i++) {
      stabs.push({
        events: chordToEvents(chords[i], CHORD_DURATION, octave),
        delayMs: startMs + i * chordSlotMs,
      })
    }
  }

  const sourceStartMs = 0
  emitSection(plan.sourceProgression, sourceStartMs, DEFAULT_OCTAVE)

  const modulationStartMs = sourceStartMs + phaseMs + SECTION_GAP_MS
  const modulationOctave = plan.technique === 'parallel' ? PARALLEL_VOICE_OCTAVE : DEFAULT_OCTAVE
  emitSection(plan.modulationProgression, modulationStartMs, modulationOctave)

  const destinationStartMs = modulationStartMs + phaseMs + SECTION_GAP_MS
  emitSection(plan.destinationProgression, destinationStartMs, DEFAULT_OCTAVE)

  return {
    stabs,
    phaseBoundaries: {
      sourceStartMs,
      modulationStartMs,
      destinationStartMs,
      endMs: destinationStartMs + phaseMs,
    },
  }
}
